import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Play, Clock, Package, Percent, Scale, Eye } from 'lucide-react';
import { Knife, Fire, Cow, Bird, MeatIcon } from '@phosphor-icons/react';
import { processingAPI, productsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatNumber, getCategoryLabel } from '../lib/utils';

export default function Processing() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isExecuteDialogOpen, setIsExecuteDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  
  // Template form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    source_product_id: '',
    fire_percentage: 10,
    outputs: []
  });
  
  // Execute form
  const [executeForm, setExecuteForm] = useState({
    template_id: '',
    source_quantity: '',
    actual_fire_percentage: '',
    notes: ''
  });
  
  // New output item
  const [newOutput, setNewOutput] = useState({
    product_id: '',
    quantity: ''  // kg olarak miktar
  });

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager' || isAdmin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, productsRes, historyRes, summaryRes] = await Promise.all([
        processingAPI.getTemplates(),
        productsAPI.getAll(),
        processingAPI.getHistory(30),
        processingAPI.getSummary(30),
      ]);
      setTemplates(templatesRes.data);
      setProducts(productsRes.data);
      setHistory(historyRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Karkas/gövde ürünleri
  const karkasProducts = products.filter(p => p.category === 'karkas');
  // İşlenmiş et ürünleri
  const outputProducts = products.filter(p => p.category === 'islenenmis' || p.category === 'gida' || p.category === 'et');

  const handleAddOutput = () => {
    if (!newOutput.product_id || !newOutput.quantity) {
      toast.error('Ürün ve miktar (kg) girin');
      return;
    }
    
    const product = products.find(p => p.id === newOutput.product_id);
    if (!product) return;
    
    // Aynı ürün var mı kontrol et
    if (templateForm.outputs.find(o => o.product_id === newOutput.product_id)) {
      toast.error('Bu ürün zaten ekli');
      return;
    }
    
    // Toplam kg kontrolü (10 kg üzerinden)
    const currentTotalKg = templateForm.outputs.reduce((sum, o) => sum + parseFloat(o.quantity_per_unit || 0), 0);
    const newQty = parseFloat(newOutput.quantity);
    
    if (currentTotalKg + newQty > 10) {
      toast.error(`Toplam çıktı 10 kg'ı geçemez (mevcut: ${currentTotalKg} kg)`);
      return;
    }
    
    setTemplateForm({
      ...templateForm,
      outputs: [...templateForm.outputs, {
        product_id: newOutput.product_id,
        product_name: product.name,
        unit: product.unit,
        quantity_per_unit: newQty,
        percentage: (newQty / 10) * 100  // Otomatik yüzde hesapla
      }]
    });
    setNewOutput({ product_id: '', quantity: '' });
  };

  const handleRemoveOutput = (productId) => {
    setTemplateForm({
      ...templateForm,
      outputs: templateForm.outputs.filter(o => o.product_id !== productId)
    });
  };

  const handleCreateTemplate = async () => {
    if (!templateForm.name || !templateForm.source_product_id || templateForm.outputs.length === 0) {
      toast.error('Şablon adı, hammadde ve en az bir çıktı gerekli');
      return;
    }

    try {
      await processingAPI.createTemplate({
        name: templateForm.name,
        source_product_id: templateForm.source_product_id,
        fire_percentage: parseFloat(templateForm.fire_percentage) || 0,
        outputs: templateForm.outputs.map(o => ({
          product_id: o.product_id,
          quantity_per_unit: o.quantity_per_unit  // kg olarak gönder
        }))
      });
      toast.success('Şablon oluşturuldu');
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şablon oluşturulamadı');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;

    try {
      await processingAPI.deleteTemplate(id);
      toast.success('Şablon silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const handleOpenExecute = (template) => {
    setSelectedTemplate(template);
    setExecuteForm({
      template_id: template.id,
      source_quantity: '',
      actual_fire_percentage: template.fire_percentage.toString(),
      notes: ''
    });
    setIsExecuteDialogOpen(true);
  };

  const handleExecuteProcessing = async () => {
    if (!executeForm.source_quantity || parseFloat(executeForm.source_quantity) <= 0) {
      toast.error('Geçerli bir miktar girin');
      return;
    }

    try {
      const result = await processingAPI.execute({
        template_id: executeForm.template_id,
        source_quantity: parseFloat(executeForm.source_quantity),
        actual_fire_percentage: executeForm.actual_fire_percentage ? parseFloat(executeForm.actual_fire_percentage) : null,
        notes: executeForm.notes || null
      });
      
      toast.success(
        `İşleme tamamlandı! ${result.data.source_quantity} ${result.data.source_unit} işlendi, ${result.data.total_output_quantity.toFixed(2)} ${result.data.source_unit} çıktı elde edildi.`
      );
      setIsExecuteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşleme başarısız');
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      source_product_id: '',
      fire_percentage: 10,
      outputs: []
    });
    setNewOutput({ product_id: '', quantity: '' });
  };

  // KG bazlı hesaplamalar (10 kg üzerinden)
  const totalOutputKg = templateForm.outputs.reduce((sum, o) => sum + (o.quantity_per_unit || 0), 0);
  const fireKg = Math.max(0, 10 - totalOutputKg);
  const totalOutputPercentage = (totalOutputKg / 10) * 100;
  const totalPercentage = 100; // Her zaman 10 kg

  // Hesaplanan çıktılar (execute form için)
  const calculatedOutputs = selectedTemplate?.outputs.map(o => ({
    ...o,
    calculated_quantity: o.quantity_per_unit 
      ? (parseFloat(executeForm.source_quantity) || 0) * (o.quantity_per_unit / 10)
      : (parseFloat(executeForm.source_quantity) || 0) * (o.percentage / 100)
  })) || [];

  const calculatedFire = selectedTemplate?.fire_kg
    ? (parseFloat(executeForm.source_quantity) || 0) * (selectedTemplate.fire_kg / 10)
    : (parseFloat(executeForm.source_quantity) || 0) * ((parseFloat(executeForm.actual_fire_percentage) || 0) / 100);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
              <Knife size={32} className="text-primary" />
              Hazırlık Mutfağı
            </h1>
            <p className="text-muted-foreground mt-1">Karkas et ve tavuk işleme, parçalama</p>
          </div>
          
          {isManager && (
            <Dialog open={isTemplateDialogOpen} onOpenChange={(open) => {
              setIsTemplateDialogOpen(open);
              if (!open) resetTemplateForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-template-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Şablon
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Yeni İşleme Şablonu</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Şablon Adı</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                      placeholder="örn: Dana Karkas Parçalama"
                      data-testid="template-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Hammadde (Karkas/Gövde)</Label>
                    <Select 
                      value={templateForm.source_product_id} 
                      onValueChange={(v) => setTemplateForm({ ...templateForm, source_product_id: v })}
                    >
                      <SelectTrigger data-testid="source-product-select">
                        <SelectValue placeholder="Hammadde seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {karkasProducts.length === 0 ? (
                          <SelectItem value="none" disabled>
                            Karkas/Gövde kategorisinde ürün yok
                          </SelectItem>
                        ) : (
                          karkasProducts.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.warehouse_stock} {p.unit} mevcut)
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {karkasProducts.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Önce "Ürünler" sayfasında "Karkas/Gövde" kategorisinde ürün ekleyin.
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label>Varsayılan Fire Oranı (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={templateForm.fire_percentage}
                      onChange={(e) => setTemplateForm({ ...templateForm, fire_percentage: e.target.value })}
                      placeholder="10"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Kemik, yağ, kayıp oranı. İşleme sırasında manuel değiştirilebilir.
                    </p>
                  </div>
                  
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold">Çıkan Ürünler (10 kg Hammadde Üzerinden)</Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      10 kg hammaddeden kaç kg ürün çıkacağını belirleyin.
                    </p>
                    
                    {/* Added outputs */}
                    {templateForm.outputs.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {templateForm.outputs.map((output) => (
                          <div key={output.product_id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{output.product_name}</span>
                              <Badge variant="secondary">{output.quantity_per_unit} kg</Badge>
                              <Badge variant="outline">{((output.quantity_per_unit / 10) * 100).toFixed(0)}%</Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveOutput(output.product_id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}}
                    
                    {/* Add new output */}
                    <div className="flex gap-2">
                      <Select value={newOutput.product_id} onValueChange={(v) => setNewOutput({ ...newOutput, product_id: v })}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Çıktı ürünü seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {outputProducts.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0.1"
                        max="10"
                        step="0.1"
                        value={newOutput.quantity}
                        onChange={(e) => setNewOutput({ ...newOutput, quantity: e.target.value })}
                        placeholder="kg"
                        className="w-24"
                      />
                      <Button type="button" onClick={handleAddOutput} variant="outline">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      10 kg hammaddeden kaç kg çıkacağını girin
                    </p>
                  </div>
                  
                  {/* Summary */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Çıktılar Toplamı:</span>
                      <span className="font-semibold">{totalOutputKg.toFixed(1)} kg ({totalOutputPercentage}%)</span>
                    </div>
                    <div className="flex justify-between items-center text-orange-600">
                      <span className="text-sm">Fire (Kalan):</span>
                      <span className="font-semibold">{fireKg.toFixed(1)} kg ({templateForm.fire_percentage || fireKg * 10}%)</span>
                    </div>
                    <div className="flex justify-between items-center border-t mt-2 pt-2">
                      <span className="text-sm font-semibold">TOPLAM:</span>
                      <span className={`font-bold ${totalOutputKg > 10 ? 'text-destructive' : totalOutputKg === 10 ? 'text-green-600' : ''}`}>
                        10 kg
                      </span>
                    </div>
                    {totalOutputKg < 10 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Kalan {(10 - totalOutputKg).toFixed(1)} kg için daha fazla çıktı ekleyebilir veya fire olarak bırakabilirsiniz.
                      </p>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleCreateTemplate} 
                    className="w-full" 
                    disabled={totalOutputKg > 10}
                    data-testid="save-template-btn"
                  >
                    Şablonu Kaydet
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Scale className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Toplam İşlenen</p>
                    <p className="text-2xl font-bold">{formatNumber(summary.total_processed || 0)} kg</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <Package className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Toplam Çıktı</p>
                    <p className="text-2xl font-bold">{formatNumber(summary.total_output || 0)} kg</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <Fire size={20} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Toplam Fire</p>
                    <p className="text-2xl font-bold">{formatNumber(summary.total_waste || 0)} kg</p>
                    <p className="text-xs text-muted-foreground">Ort. %{summary.avg_waste_percentage || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/10 rounded-lg">
                    <Percent className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Fire Maliyeti</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.total_waste_cost || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="templates" className="space-y-6">
          <TabsList>
            <TabsTrigger value="templates" data-testid="tab-templates">Şablonlar</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">İşleme Geçmişi</TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-full flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : templates.length === 0 ? (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  Henüz işleme şablonu oluşturulmamış
                </div>
              ) : (
                templates.map((template) => (
                  <Card key={template.id} className="relative" data-testid={`template-card-${template.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {template.source_product_name.toLowerCase().includes('tavuk') ? (
                            <Bird size={20} className="text-amber-600" />
                          ) : (
                            <Cow size={20} className="text-red-600" />
                          )}
                          {template.name}
                        </CardTitle>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="p-2 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Hammadde</p>
                          <p className="font-semibold">{template.source_product_name}</p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">Çıktılar</p>
                          <div className="space-y-1">
                            {template.outputs.map((output, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>{output.product_name}</span>
                                <Badge variant="outline">{output.percentage}%</Badge>
                              </div>
                            ))}
                            <div className="flex justify-between text-sm text-orange-600">
                              <span>Fire</span>
                              <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20">
                                {template.fire_percentage}%
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {isManager && (
                          <Button 
                            onClick={() => handleOpenExecute(template)} 
                            className="w-full mt-2"
                            data-testid={`execute-btn-${template.id}`}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            İşleme Yap
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Son İşlemeler
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Henüz işleme kaydı yok
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Şablon</TableHead>
                        <TableHead>Hammadde</TableHead>
                        <TableHead className="text-right">Giriş</TableHead>
                        <TableHead className="text-right">Çıkış</TableHead>
                        <TableHead className="text-right">Fire</TableHead>
                        <TableHead>İşleyen</TableHead>
                        <TableHead className="text-center">Detay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((record) => (
                        <TableRow key={record.id} data-testid={`history-row-${record.id}`}>
                          <TableCell className="text-sm">
                            {new Date(record.processed_at).toLocaleDateString('tr-TR')}
                          </TableCell>
                          <TableCell className="font-medium">{record.template_name}</TableCell>
                          <TableCell>{record.source_product_name}</TableCell>
                          <TableCell className="text-right font-data">
                            {formatNumber(record.source_quantity)} {record.source_unit}
                          </TableCell>
                          <TableCell className="text-right font-data text-green-600">
                            {formatNumber(record.total_output_quantity)} {record.source_unit}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-orange-600 font-data">
                              {formatNumber(record.fire_quantity)} {record.source_unit}
                            </span>
                            <span className="text-xs text-muted-foreground ml-1">
                              (%{record.fire_percentage})
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {record.processed_by_name}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedRecord(record);
                                setIsDetailDialogOpen(true);
                              }}
                              data-testid={`detail-btn-${record.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Execute Dialog */}
        <Dialog open={isExecuteDialogOpen} onOpenChange={setIsExecuteDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Knife size={24} className="text-primary" />
                İşleme Yap
              </DialogTitle>
            </DialogHeader>
            
            {selectedTemplate && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Şablon</p>
                  <p className="font-semibold">{selectedTemplate.name}</p>
                </div>
                
                <div>
                  <Label>İşlenecek Miktar ({selectedTemplate.source_unit})</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={executeForm.source_quantity}
                    onChange={(e) => setExecuteForm({ ...executeForm, source_quantity: e.target.value })}
                    placeholder={`örn: 50`}
                    data-testid="execute-quantity-input"
                  />
                  {selectedTemplate && products.find(p => p.id === selectedTemplate.source_product_id) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Mevcut stok: {formatNumber(products.find(p => p.id === selectedTemplate.source_product_id)?.warehouse_stock || 0)} {selectedTemplate.source_unit}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label>Gerçek Fire Oranı (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={executeForm.actual_fire_percentage}
                    onChange={(e) => setExecuteForm({ ...executeForm, actual_fire_percentage: e.target.value })}
                    placeholder={`Varsayılan: ${selectedTemplate.fire_percentage}%`}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    İşleme sonrası gerçek fire oranını girin. Boş bırakılırsa varsayılan kullanılır.
                  </p>
                </div>
                
                <div>
                  <Label>Not (Opsiyonel)</Label>
                  <Input
                    value={executeForm.notes}
                    onChange={(e) => setExecuteForm({ ...executeForm, notes: e.target.value })}
                    placeholder="İşleme ile ilgili not..."
                  />
                </div>
                
                {/* Preview */}
                {parseFloat(executeForm.source_quantity) > 0 && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-semibold mb-2">Tahmini Çıktılar</p>
                    <div className="space-y-1 text-sm">
                      {calculatedOutputs.map((output, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span>{output.product_name}</span>
                          <span className="font-data text-green-600">
                            +{formatNumber(output.calculated_quantity)} {output.unit}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between text-orange-600">
                        <span>Fire</span>
                        <span className="font-data">
                          -{formatNumber(calculatedFire)} {selectedTemplate.source_unit}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={handleExecuteProcessing} 
                  className="w-full"
                  data-testid="confirm-execute-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  İşlemeyi Başlat
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Detail Dialog - İşleme Detayı */}
        <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                İşleme Detayı
              </DialogTitle>
            </DialogHeader>
            
            {selectedRecord && (
              <div className="space-y-4 mt-4">
                {/* Özet Bilgiler */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Tarih</p>
                    <p className="font-semibold">{new Date(selectedRecord.processed_at).toLocaleDateString('tr-TR')}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">İşleyen</p>
                    <p className="font-semibold">{selectedRecord.processed_by_name}</p>
                  </div>
                </div>
                
                {/* Hammadde Bilgisi */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Cow size={20} className="text-blue-600" />
                    <span className="font-semibold text-blue-800">Hammadde</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">{selectedRecord.source_product_name || selectedRecord.original_product}</span>
                    <Badge variant="secondary" className="text-lg">
                      {formatNumber(selectedRecord.source_quantity || selectedRecord.original_quantity)} {selectedRecord.source_unit || 'kg'}
                    </Badge>
                  </div>
                </div>
                
                {/* Çıkan Ürünler */}
                <div className="border rounded-lg">
                  <div className="p-3 bg-green-50 border-b border-green-200">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-green-800">Çıkan Ürünler</span>
                      <Badge variant="outline" className="ml-auto">
                        {selectedRecord.outputs?.length || 0} kalem
                      </Badge>
                    </div>
                  </div>
                  <div className="p-3">
                    {selectedRecord.outputs && selectedRecord.outputs.length > 0 ? (
                      <div className="space-y-2">
                        {selectedRecord.outputs.map((output, idx) => {
                          const percentage = selectedRecord.source_quantity 
                            ? ((output.quantity / selectedRecord.source_quantity) * 100).toFixed(1) 
                            : 0;
                          return (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="font-medium">{output.name}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge variant="secondary">
                                  {formatNumber(output.quantity)} {output.unit || 'kg'}
                                </Badge>
                                <span className="text-xs text-muted-foreground w-12 text-right">
                                  %{percentage}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Toplam */}
                        <div className="flex items-center justify-between p-2 bg-green-100 rounded-lg mt-3 border border-green-200">
                          <span className="font-semibold text-green-800">Toplam Çıkış</span>
                          <Badge className="bg-green-600">
                            {formatNumber(selectedRecord.total_output_quantity)} {selectedRecord.source_unit || 'kg'}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">Çıkan ürün bilgisi bulunamadı</p>
                    )}
                  </div>
                </div>
                
                {/* Fire Bilgisi */}
                {(selectedRecord.fire_quantity > 0 || selectedRecord.waste_quantity > 0) && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Fire size={20} className="text-orange-600" />
                      <span className="font-semibold text-orange-800">Fire / Kayıp</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Miktar</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                          {formatNumber(selectedRecord.fire_quantity || selectedRecord.waste_quantity)} {selectedRecord.source_unit || 'kg'}
                        </Badge>
                        <span className="text-sm text-orange-600">
                          (%{selectedRecord.fire_percentage || selectedRecord.waste_percentage || 0})
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Notlar */}
                {selectedRecord.notes && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Notlar</p>
                    <p className="text-sm">{selectedRecord.notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
